import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PaymentContext, PAYMENT_STAGES } from '../PaymentOrchestrator/PaymentContextProvider';
import { PAYMENT_ERROR_GROUPS, PAYMENT_ERROR_TYPES } from '../PaymentOrchestrator/constants/paymentErrors';
import changeBillingAuthorizedTransition from '../PaymentOrchestrator/transitions/changeBillingAuthorizedTransition';
import AuthorizationScreen from './AuthorizationScreen';
import {
  publishChallengePageRendered,
  publishChallengePageRenderError,
  publishChangeBillDate3dsCancel,
  publishChangeBillingDate3dsAuthSuccess,
  track3dsChallengeFailure,
  track3dsOutputTokenReceived,
} from '../../helpers/changeBillingDate/analytics';
import usePageView from '../../hooks/usePageView';

const CANCEL_CODES = new Set([
  'three_ds_cancelled',
  'three_ds_canceled',
  'three_d_secure_action_cancelled',
  'three_d_secure_action_canceled',
  'three_ds_challenge_cancelled',
  'three_ds_challenge_canceled',
  'user_cancelled',
  'user_canceled',
  'cancelled',
  'canceled',
  'cancel',
  'abort',
  'aborted',
]);

const PAGE_NAME = 'ProdMoveChangeBillingDateAuthorizationPage';

const ChangeBillingAuthorization = () => {
  const paymentContext = useContext(PaymentContext);
  const { tokens, addPaymentToken, raisePaymentError, clearPaymentErrors, goToPaymentStage, isWebview } =
    paymentContext;
  const selectedBillDate = useSelector(state => state.billingDateSelection?.selectedBillDate);

  const dispatch = useDispatch();
  const cancelledRef = useRef(false);

  usePageView(PAGE_NAME);

  useEffect(() => {
    window.scrollTo(0, 0);
    publishChallengePageRendered();
  }, []);

  const isThreeDsCancel = useCallback(error => {
    const candidates = [
      typeof error === 'string' ? error : undefined,
      error?.code,
      error?.type,
      error?.name,
      error?.errorCode,
      error?.message,
      error?.reason,
      error?.details?.message,
      error?.details?.reason,
      error?.details?.code,
      error?.details?.type,
      error?.details?.errorCode,
      error?.error?.code,
      error?.error?.type,
      error?.error?.message,
      error?.error?.reason,
      error?.error?.errorCode,
      error?.details?.error?.code,
      error?.details?.error?.type,
      error?.details?.error?.message,
      error?.details?.error?.reason,
      error?.details?.error?.errorCode,
    ];

    return candidates.some(candidate => {
      if (typeof candidate !== 'string') return false;
      const normalized = candidate.toLowerCase();
      return CANCEL_CODES.has(normalized) || normalized.includes('cancel') || normalized.includes('abort');
    });
  }, []);

  const handleCancel = useCallback(() => {
    cancelledRef.current = true;
    publishChangeBillDate3dsCancel();
    clearPaymentErrors?.();
    addPaymentToken({ paymentMethodToken: null, authorizationInput: null, authorizationOutput: null });
    goToPaymentStage(PAYMENT_STAGES.REVIEW_AND_PAY);
  }, [addPaymentToken, clearPaymentErrors, goToPaymentStage]);

  const handleSuccess = useCallback(
    outputToken => {
      if (cancelledRef.current) return;

      publishChangeBillingDate3dsAuthSuccess();

      track3dsOutputTokenReceived();
      // After 3DS challenge success, finalize the billing date change with the authorization token
      changeBillingAuthorizedTransition(paymentContext, outputToken.id, selectedBillDate)(dispatch);
    },
    [dispatch, paymentContext, selectedBillDate]
  );

  const handleFailure = useCallback(
    error => {
      if (cancelledRef.current) return;

      publishChallengePageRenderError(error?.code);

      track3dsChallengeFailure(error?.code, error?.message);

      cancelledRef.current = true;
      if (isThreeDsCancel(error)) {
        handleCancel();
        return;
      }

      clearPaymentErrors?.();
      raisePaymentError({
        message: error?.message,
        group: PAYMENT_ERROR_GROUPS.AUTHORIZATION,
        type: PAYMENT_ERROR_TYPES.AUTHORIZATION.UNSUCCESSFUL_3DS,
      });
      goToPaymentStage(PAYMENT_STAGES.CHANGE_BILLING_DATE_FAILURE);
    },
    [clearPaymentErrors, goToPaymentStage, handleCancel, isThreeDsCancel, raisePaymentError]
  );

  return (
    <AuthorizationScreen
      actionTokenId={tokens.authorizationInput}
      onToken={handleSuccess}
      onError={handleFailure}
      isWebview={isWebview}
    />
  );
};

export default ChangeBillingAuthorization;
